import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Prohibit, MagnifyingGlass, Warning, ShieldWarning, Info, ArrowLeft, Cigarette, Pill, Drop, Atom, Lightning, Knife } from '@phosphor-icons/react';
import DropioLogo from '@/components/DropioLogo';

const RESTRICTED_ITEMS = [
  { name: 'Weapons & Firearms', category: 'prohibited', icon: Knife, desc: 'Guns, knives, ammunition, explosives — banned globally.', countries: 'All' },
  { name: 'Narcotics & Drugs', category: 'prohibited', icon: Pill, desc: 'Cocaine, heroin, marijuana, illegal substances — banned globally.', countries: 'All' },
  { name: 'Explosives & Flammables', category: 'prohibited', icon: Lightning, desc: 'Fireworks, propane, anything flammable or explosive.', countries: 'All' },
  { name: 'Lithium Batteries (loose)', category: 'restricted', icon: Lightning, desc: 'Allowed if installed in device. Loose batteries restricted on most airlines.', countries: 'Most airlines' },
  { name: 'Alcohol', category: 'restricted', icon: Drop, desc: 'Restricted in Muslim-majority countries.', countries: 'Bangladesh, UAE, Saudi Arabia, Iran' },
  { name: 'Tobacco & E-cigarettes', category: 'restricted', icon: Cigarette, desc: 'Subject to customs duties, declaration required.', countries: 'Bangladesh, Australia, India' },
  { name: 'Prescription Medicines', category: 'regulated', icon: Pill, desc: 'Require prescription documentation. Some substances prohibited.', countries: 'All — varies by drug' },
  { name: 'Currency (large amounts)', category: 'regulated', icon: Atom, desc: 'Amounts over $10,000 USD equivalent must be declared.', countries: 'All' },
  { name: 'Fresh Food / Produce', category: 'restricted', icon: Drop, desc: 'Quarantine restrictions on meat, dairy, fresh fruit.', countries: 'Australia, New Zealand, USA' },
  { name: 'Drones & RC Equipment', category: 'restricted', icon: Atom, desc: 'May require license at destination.', countries: 'India, Pakistan, UAE' },
  { name: 'Counterfeit goods', category: 'prohibited', icon: Warning, desc: 'Fake branded items — confiscated at customs.', countries: 'All' },
  { name: 'Live animals / plants', category: 'prohibited', icon: Drop, desc: 'Strict biosecurity laws apply.', countries: 'All' }
];

const CATEGORY_STYLES = {
  prohibited: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'bg-red-100 text-red-600', label: 'Prohibited' },
  restricted: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'bg-orange-100 text-orange-600', label: 'Restricted' },
  regulated: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: 'bg-yellow-100 text-yellow-600', label: 'Regulated' }
};

const RestrictedGoodsPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = RESTRICTED_ITEMS.filter((item) => {
    const matchesSearch = !search || item.name.toLowerCase().includes(search.toLowerCase()) || item.desc.toLowerCase().includes(search.toLowerCase()) || item.countries.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || item.category === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <button onClick={() => navigate('/')} data-testid="rg-logo"><DropioLogo size={36} /></button>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-700 font-semibold text-sm" data-testid="rg-back">
            <ArrowLeft size={18} weight="bold" /> Back
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center flex-shrink-0">
              <ShieldWarning size={28} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="rg-heading">
                Restricted & Prohibited Items
              </h1>
              <p className="text-gray-700 max-w-2xl">
                Before sending or accepting a parcel, review what's allowed across borders. Our AI compliance engine enforces these rules automatically.
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-2xl">
            <MagnifyingGlass size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" placeholder="Search items, e.g. 'lithium battery' or country..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100 shadow-sm"
              data-testid="rg-search"
            />
          </div>
        </div>
      </section>

      {/* Filters + List */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'all', label: 'All items' },
              { id: 'prohibited', label: 'Prohibited', color: 'red' },
              { id: 'restricted', label: 'Restricted', color: 'orange' },
              { id: 'regulated', label: 'Regulated', color: 'yellow' }
            ].map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  filter === f.id ? 'dropio-gradient-cta text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`rg-filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No items match your search.</div>
            ) : (
              filtered.map((item, idx) => {
                const style = CATEGORY_STYLES[item.category];
                const Icon = item.icon;
                return (
                  <div key={idx} className={`rounded-xl border ${style.border} ${style.bg} p-5 flex items-start gap-4`} data-testid={`rg-item-${idx}`}>
                    <div className={`w-12 h-12 rounded-lg ${style.icon} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={24} weight="duotone" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">{item.name}</h3>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${style.icon}`}>{style.label}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-1.5">{item.desc}</p>
                      <p className="text-xs text-gray-500"><strong>Countries:</strong> {item.countries}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-10 p-5 bg-blue-50 rounded-xl flex items-start gap-3 border border-blue-100">
            <Info size={20} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900 text-sm mb-1">Official guidance</p>
              <p className="text-sm text-gray-700">
                Always verify with the destination country's customs authority. Dropio's compliance engine is a first-line filter and does not replace official regulations. When in doubt, contact your airline or customs broker.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RestrictedGoodsPage;
